import clsx from "clsx";
import { createContext, useContext, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { DrawerFloating } from "./drawerFloating";

export interface DrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
  setOpen: (open: boolean) => void;
  variant?: DrawerVariant;
  direction?: "left" | "right";
  disableClose?: boolean;
  floatingPortalId?: string;
}

export type DrawerVariant = "light" | "colored" | "dark";

export interface DrawerContextState {
  open: boolean;
  setOpen: (open: boolean) => void;
  variant: DrawerVariant;
  float?: boolean;
}

export const DrawerContext = createContext<DrawerContextState>({
  open: true,
  setOpen: () => {},
  variant: "light",
});

export const useDrawerContext = () => useContext(DrawerContext);

export interface DrawerContextProviderProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  variant?: DrawerVariant;
  floatingPortalId?: string;
  children: React.ReactNode;
}

export default function Drawer(props: Readonly<DrawerProps>) {
  const {
    className,
    open,
    setOpen,
    floatingPortalId,
    variant = "dark",
    disableClose = false,
    direction = "left",
    ...rest
  } = props;

  const float = !!floatingPortalId;

  const context = useMemo(
    () => ({ open, setOpen, variant, float }),
    [open, setOpen, variant, float],
  );

  let Content = (
    <div
      {...rest}
      className={twMerge(
        clsx(
          "shrink-0 flex flex-col",
          "h-screen overflow-y-auto",
          "transition-[width] duration-300",
          {
            "w-64": open || float,
            "w-24": !open && !float,
            "bg-primary-light text-primary-dark": variant === "light",
            "bg-primary text-white": variant === "colored",
            "bg-secondary-dark text-white": variant === "dark",
            "sticky top-0": !float,
          },
        ),
        className,
      )}
    />
  );

  if (float) {
    Content = (
      <DrawerFloating
        floatingPortalId={floatingPortalId}
        disableClose={disableClose}
        direction={direction}
        {...rest}
      >
        {Content}
      </DrawerFloating>
    );
  }

  return (
    <DrawerContext.Provider value={context}>{Content}</DrawerContext.Provider>
  );
}
